import { z } from 'zod';

import { RoleRefSchema, SupplierRefSchema } from './external-refs.js';

/**
 * Aggregate 2 — a contract's factor-driven budget.
 *
 * Spec: docs/specs/domain-model.md (Aggregate 2). Category AMOUNTS are canonical
 * (BR-8): percentages, costs, margins and "not allocated" are always derived
 * (CALC-9..14) and never stored.
 */

export const CURRENT_CONTRACT_SCHEMA_VERSION = 1;

export const ContractTypeSchema = z.enum(['fixed', 'tm', 'retainer']);
export type ContractType = z.infer<typeof ContractTypeSchema>;

export const ContractStatusSchema = z.enum(['draft', 'active']);
export type ContractStatus = z.infer<typeof ContractStatusSchema>;

export const CATEGORY_IDS = ['own', 'cont', 'exp', 'sub', 'mat'] as const;
export const CategoryIdSchema = z.enum(CATEGORY_IDS);
export type CategoryId = z.infer<typeof CategoryIdSchema>;

/**
 * Static category definitions: label + default target factor (from the design).
 *
 * Since budget-model schema 2 the factors here are the **fallback** only — the
 * live per-section defaults ride the flag payload (`uplift.<id>.default`) and
 * reach a new contract as `seed.categoryFactors`. These values are what a
 * caller with no budget-model config gets, and `CONTROL_BUDGET_MODEL` restates
 * them exactly so the control variant seeds identically.
 */
export const CATEGORY_DEFS: Record<CategoryId, { label: string; defaultFactor: number }> = {
  own: { label: 'Own Work', defaultFactor: 2.8 },
  cont: { label: 'Contingency', defaultFactor: 2.1 },
  exp: { label: 'Expenses', defaultFactor: 1.2 },
  sub: { label: 'Sub-contractors', defaultFactor: 1.5 },
  mat: { label: 'Materials', defaultFactor: 1.3 },
};

/**
 * Categories that carry NO cost (BR-14, 2026-10-12): Contingency is a buffer
 * on the revenue side — its amount is distributed like any other category's,
 * but nothing is spent to earn it, so its cost is 0 (CALC-9), it has no uplift
 * to manage (no factor pill, whatever the budget-model config says for it),
 * and its card shows no budgeted-cost line. The stored `targetFactor` on such
 * a category is inert.
 */
export const COSTLESS_CATEGORY_IDS: readonly CategoryId[] = ['cont'];
export const categoryCarriesCost = (id: CategoryId): boolean => !COSTLESS_CATEGORY_IDS.includes(id);

/** Default contingency share of revenue seeded by the amount-first flow (BR-14). */
export const DEFAULT_CONTINGENCY_SHARE = 0.1;

/** Contract-level target factor used as display threshold (design: ×2.1). */
export const DEFAULT_TARGET_FACTOR = 2.1;

const sharePct = z.number().min(0).max(100).multipleOf(0.01);

export const SplitRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  /** Percent of the parent line — informational in v1 (BR-19). */
  sharePct,
});
export type SplitRow = z.infer<typeof SplitRowSchema>;

export const SupplierSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  /** Set when the supplier was picked from the register; blank lines have none (BR-13). */
  supplierRef: SupplierRefSchema.optional(),
  sharePct,
  types: z.array(SplitRowSchema).default([]),
});
export type Supplier = z.infer<typeof SupplierSchema>;

export const CategorySplitSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('rows'), rows: z.array(SplitRowSchema).default([]) }),
  z.object({
    kind: z.literal('suppliers'),
    suppliers: z.array(SupplierSchema).default([]),
    unassigned: z.array(SplitRowSchema).default([]),
  }),
]);
export type CategorySplit = z.infer<typeof CategorySplitSchema>;

export const CategoryBudgetSchema = z.object({
  id: CategoryIdSchema,
  /** Revenue attributed to this category, whole currency units — CANONICAL (BR-8). */
  amount: z.number().int().min(0),
  /** Revenue ÷ cost for this category. Margin = 1 − 1/factor (CALC-10). */
  targetFactor: z.number().min(0.1).max(100),
  /** Display preference for unused categories; requires amount = 0 (BR-9). */
  hidden: z.boolean().default(false),
  /** Set when this category is billed via another contract of the project (BR-10). */
  billedVia: z.string().uuid().optional(),
  /**
   * 'own' only. Hours the user typed. Until set, hours derive from cost ÷ rate
   * (CALC-11); once set they are the user's number and are never recalculated
   * (sticky, D11). The factor is never derived from them — amount, factor and
   * hours are three independent user-owned values, and the cost per hour is the
   * derived quantity (CALC-23).
   */
  ownHoursOverride: z.number().min(0).max(1_000_000).multipleOf(0.01).optional(),
  split: CategorySplitSchema.optional(),
});
export type CategoryBudget = z.infer<typeof CategoryBudgetSchema>;

/**
 * One role on a T&M contract's rate card, keyed by the role's hourly-rate
 * ServiceID (`roleRef.tlpId`). A single amount for now: TimeLog exposes only
 * the currently valid price, so the time-phased schedule is recorded as intent
 * (D3) and lands when upstream exposes it.
 */
export const RateCardEntrySchema = z.object({
  id: z.string().uuid(),
  roleRef: RoleRefSchema,
  /** Sell rate, currency/h. Tasks mapped to this contract price by role (BR-11). */
  rate: z.number().min(0).max(1_000_000),
  /** Cost rate, currency/h, for role-only lines (CALC-21). Persons carry their own. */
  costRate: z.number().min(0).max(1_000_000).optional(),
});
export type RateCardEntry = z.infer<typeof RateCardEntrySchema>;

/**
 * Only time-based contracts carry a rate card (BR-11, D10): a fixed-price
 * contract has no per-role prices — its hours price at the contract's average
 * rate (CALC-23). The document does not know its own type (that is a column on
 * the aggregate row), so the API applies this at the write boundary.
 */
export const rateCardAllowedFor = (type: ContractType): boolean => type !== 'fixed';

/**
 * What one scheduled payment is counted in. Hours and days for work billed by
 * time, pieces for anything countable (the default), km for mileage. The unit
 * is a label on the line — nothing prices by it.
 */
export const PAYMENT_UNITS = ['hours', 'days', 'pieces', 'km'] as const;
export const PaymentUnitSchema = z.enum(PAYMENT_UNITS);
export type PaymentUnit = z.infer<typeof PaymentUnitSchema>;
export const DEFAULT_PAYMENT_UNIT: PaymentUnit = 'pieces';

/**
 * What the PM records about a payment: it is still to be invoiced, or it has
 * been. `overdue` is never stored — it is an open payment whose invoice date
 * has passed, read off the calendar (`paymentStatusOn`), so it clears itself
 * the moment the payment is marked invoiced or its date is moved.
 */
export const PAYMENT_STATUSES = ['open', 'invoiced'] as const;
export const PaymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;
export const DEFAULT_PAYMENT_STATUS: PaymentStatus = 'open';

/** The three states a payment shows: the stored two plus the derived one. */
export type PaymentDisplayStatus = PaymentStatus | 'overdue';

/**
 * One line of a fixed-price contract's payment schedule (BR-31): a commercial
 * intent to invoice the customer for `amount` on `invoiceDate`. Quantity, unit,
 * rate and discount describe the line and prefill the amount (CALC-26) — the
 * stored amount is the PM's number and is never recalculated from them.
 */
export const PaymentSchema = z.object({
  id: z.string().uuid(),
  /** Calendar day (ISO `YYYY-MM-DD`), never an instant. */
  invoiceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** The line's text on the invoice ("Upfront payment before start"); absent until typed. */
  invoiceText: z.string().max(500).optional(),
  quantity: z.number().min(0).max(1_000_000).multipleOf(0.01),
  unit: PaymentUnitSchema.default(DEFAULT_PAYMENT_UNIT),
  /** Currency per unit. */
  rate: z.number().min(0).max(100_000_000),
  discountPct: z.number().min(0).max(100),
  /** The invoiced amount, whole currency units — CANONICAL, like a category amount (BR-8). */
  amount: z.number().int().min(0),
  status: PaymentStatusSchema.default(DEFAULT_PAYMENT_STATUS),
});
export type Payment = z.infer<typeof PaymentSchema>;

/**
 * A payment's status on `today` (ISO date): invoiced as recorded, otherwise
 * overdue once its invoice date has passed, otherwise open. A payment written
 * before the status existed carries none and reads as open.
 */
export const paymentStatusOn = (
  payment: Pick<Payment, 'invoiceDate'> & { status?: PaymentStatus },
  today: string,
): PaymentDisplayStatus => {
  if (payment.status === 'invoiced') return 'invoiced';
  return payment.invoiceDate < today ? 'overdue' : 'open';
};

export const ContractDocumentSchema = z
  .object({
    schemaVersion: z.literal(CURRENT_CONTRACT_SCHEMA_VERSION),
    currency: z.string().length(3).default('DKK'),
    /** The agreed/expected contract amount, whole currency units. */
    revenue: z.number().int().min(0),
    targetFactor: z.number().min(0.1).max(100).default(DEFAULT_TARGET_FACTOR),
    categories: z.array(CategoryBudgetSchema),
    /** T&M contracts own a rate card; fixed price carries none (BR-11, `rateCardAllowedFor`). */
    rateCard: z.array(RateCardEntrySchema).default([]),
    /** Stored + rendered as net rates; no financial effect in v1 (OQ-3). */
    tmDiscountPct: z.number().min(0).max(100).optional(),
    /**
     * The rate-card entry the PM picked as the T&M contract's DEFAULT hourly
     * rate — the "own amount = hours × rate" convenience on Own Work. Absent
     * until picked: a seeded card selects nothing by itself (decided
     * 2026-09-15), and the choice was never derived from the amounts.
     */
    tmDefaultRateEntryId: z.string().uuid().optional(),
    /**
     * The scheduled payments of a FIXED-PRICE contract (BR-31), in the order
     * entered. The payment schedule is the list itself, not a thing of its own:
     * a document with no `payments` has an empty schedule, which is what every
     * document written before the field existed has — read it through
     * `paymentsOf`, never directly, because stored documents are handed back as
     * they were written and carry no key until the first payment is added. The
     * document does not know its own type and the UI only offers the schedule
     * on a fixed contract; payments on a document whose contract moves to T&M
     * are kept, not dropped — they are the PM's data and come back with the type.
     */
    payments: z.array(PaymentSchema).optional(),
  })
  .superRefine((document, ctx) => {
    const issue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: 'custom', path, message });

    // Exactly the five known categories, each once.
    const ids = document.categories.map((category) => category.id);
    for (const required of CATEGORY_IDS) {
      if (!ids.includes(required)) {
        issue(['categories'], `Missing category '${required}' — all five must be present`);
      }
    }
    if (new Set(ids).size !== ids.length) {
      issue(['categories'], 'Duplicate category ids');
    }

    document.categories.forEach((category, i) => {
      if (category.hidden && category.amount !== 0) {
        issue(
          ['categories', i, 'hidden'],
          `Cannot hide '${category.id}' while it holds budget — zero the amount first (BR-9)`,
        );
      }
      if (category.ownHoursOverride !== undefined && category.id !== 'own') {
        issue(
          ['categories', i, 'ownHoursOverride'],
          'Hours override applies to Own Work only (BR-12)',
        );
      }
      if (category.split) {
        const wantsSuppliers = category.id === 'sub';
        if (wantsSuppliers && category.split.kind !== 'suppliers') {
          issue(['categories', i, 'split'], 'Sub-contractors split uses suppliers (BR-19)');
        }
        if (!wantsSuppliers && category.split.kind !== 'rows') {
          issue(['categories', i, 'split'], `'${category.id}' split uses rows (BR-19)`);
        }
      }
    });

    // Duplicate roles on the rate card would make rate resolution ambiguous.
    const roleIds = document.rateCard.map((entry) => entry.roleRef.tlpId);
    if (new Set(roleIds).size !== roleIds.length) {
      issue(['rateCard'], 'Duplicate role on the rate card');
    }

    // Two payments with one id would be one line to the editor and two to the totals.
    const paymentIds = (document.payments ?? []).map((payment) => payment.id);
    if (new Set(paymentIds).size !== paymentIds.length) {
      issue(['payments'], 'Duplicate payment id on the schedule');
    }

    // The default hourly rate is one of the card's entries, or nothing.
    if (
      document.tmDefaultRateEntryId !== undefined &&
      !document.rateCard.some((entry) => entry.id === document.tmDefaultRateEntryId)
    ) {
      issue(['tmDefaultRateEntryId'], 'The default hourly rate must be an entry of the rate card');
    }
  });

export type ContractDocument = z.infer<typeof ContractDocumentSchema>;

/**
 * The payment schedule (BR-31): the document's payments, or none. The one way
 * to read them — a document written before payments existed carries no key.
 */
export const paymentsOf = (document: ContractDocument): readonly Payment[] =>
  document.payments ?? [];

/**
 * A fresh contract: all five categories at 0 with their default factors.
 * `hiddenCategories` seeds per-contract visibility from the budget-model config
 * (BR-27: presence is derived from availability + this override — 'own' never hides),
 * and `categoryFactors` seeds the factors from the same config (CALC-25). Both
 * are supplied by the client; this function and the API know no config.
 */
export const createEmptyContractDocument = (
  overrides: Partial<Pick<ContractDocument, 'currency' | 'targetFactor' | 'rateCard'>> & {
    hiddenCategories?: readonly CategoryId[];
    categoryFactors?: Partial<Record<CategoryId, number>>;
  } = {},
): ContractDocument => {
  const { hiddenCategories = [], categoryFactors = {}, ...documentOverrides } = overrides;
  return ContractDocumentSchema.parse({
    schemaVersion: CURRENT_CONTRACT_SCHEMA_VERSION,
    revenue: 0,
    categories: CATEGORY_IDS.map((id) => ({
      id,
      amount: 0,
      targetFactor: categoryFactors[id] ?? CATEGORY_DEFS[id].defaultFactor,
      hidden: id !== 'own' && hiddenCategories.includes(id),
    })),
    ...documentOverrides,
  });
};

export const getCategory = (document: ContractDocument, id: CategoryId): CategoryBudget => {
  const category = document.categories.find((candidate) => candidate.id === id);
  if (!category) {
    throw new Error(`Contract document is missing category '${id}'`);
  }
  return category;
};
